import { useEffect, useState } from "react";
import { CarouselForm, CarouselFormItemProps } from "@/components/Common/CarouselForm/CarouselForm";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { SignupExitModal } from "@/components/SignupPage/SignupExitModal/SignupExitModal";
import { useRoute } from "wouter";
import { PhoneNumberStepSlide } from "@/components/SignupPage/PhoneNumberStepSlide/PhoneNumberStepSlide";
import { PhoneCodeStepSlide } from "@/components/SignupPage/PhoneCodeStepSlide/PhoneCodeStepSlide";
import { WalletTypeStepSlide } from "@/components/SignupPage/WalletTypeStepSlide/WalletTypeStepSlide";
import { WalletAddressStepSlide } from "@/components/SignupPage/WalletAddressStepSlide/WalletAddressStepSlide";
import { LoginStep } from "@/components/SignupPage/LoginStep/LoginStep";
import { RoleStepSlide } from "@/components/SignupPage/RoleStepSlide/RoleStepSlide";
import { AuthStateType } from "@/types/auth";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import { INCLUDE_PHONE_VERIFICATION_STEP } from "@/constants/auth";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

export const SignUpPage = () => {
  const [, pageParams] = useRoute("/login/:section");
  const isLoggedIn = AuthMachineContext.useSelector((state) => state.context.isLoggedIn);
  const [selectedSlide, setSelectedSlide] = useState<number>();
  const [isSignupExitModalOpen, setIsSignupExitModalOpen] = useState(false);
  const [items, setItems] = useState<CarouselFormItemProps[]>();
  const { section } = pageParams || {};

  useEffect(() => {
    const newItems =
      SECTION_MAP[isLoggedIn ? AuthStateType.AUTHORISED : AuthStateType.UNAUTHORISED];
    const newSelectedSlide = newItems.findIndex(({ value }) => {
      return value === section;
    });

    setItems(newItems);
    setSelectedSlide(Math.max(0, newSelectedSlide));
  }, [isLoggedIn, section]);

  return (
    <>
      <SignupExitModal
        isOpen={isSignupExitModalOpen}
        onClose={() => {
          setIsSignupExitModalOpen(false);
        }}
        onSubmit={() => {
          document.location.href = "https://io.net";
        }}
      />
      <div className="flex flex-1 flex-col bg-black" data-testid="AuthLandingPage">
        {!isLoggedIn && (
          <div className="pl-6 pt-8 lg:pl-20">
            <a
              className="subtext-medium flex cursor-pointer flex-row items-center gap-2 text-gray-dark-1000 hover:text-white"
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                trackEvent(AnalyticsEventType.RETURN_CLICKED);
                setTimeout(() => {
                  window.location.href = "https://io.net";
                }, 100);
              }}
              href="https://io.net"
            >
              <ChevronLeftIcon className="h-[12px]" />
              <span>Return to io.net</span>
            </a>
          </div>
        )}
        <div className="flex flex-1 flex-col">
          {items && typeof selectedSlide === "number" && (
            <CarouselForm items={items} slide={selectedSlide} />
          )}
        </div>
      </div>
    </>
  );
};

const SECTION_MAP = {
  [AuthStateType.UNAUTHORISED]: [
    {
      value: AuthStateType.UNAUTHORISED,
      component: LoginStep
    }
  ],
  [AuthStateType.AUTHORISED]: [
    ...(INCLUDE_PHONE_VERIFICATION_STEP
      ? [
          {
            value: AuthStateType.ENTER_PHONE_NUMBER,
            component: PhoneNumberStepSlide
          },
          {
            value: AuthStateType.ENTER_PHONE_CODE,
            component: PhoneCodeStepSlide
          }
        ]
      : []),
    {
      value: AuthStateType.ENTER_ROLE,
      component: RoleStepSlide
    },
    {
      value: AuthStateType.ENTER_WALLET_TYPE,
      component: WalletTypeStepSlide
    },
    {
      value: AuthStateType.ENTER_WALLET_ADDRESS,
      component: WalletAddressStepSlide
    }
  ]
};

export default SignUpPage;
